import api from './authService';

// ===========================================
// Types
// ===========================================

export interface Transaction {
  id: number;
  tenant_id: number | null;
  product_id: number | null;
  user_id: number | null;
  payment_source: string;
  external_transaction_id: string | null;
  gross_amount: number;
  net_amount: number;
  fee_amount: number;
  tax_amount: number;
  currency: string;
  plan_code: string | null;
  plan_name: string | null;
  quantity: number;
  status: string;
  customer_email: string | null;
  customer_name: string | null;
  description: string | null;
  is_distributed: number;
  distributed_at: string | null;
  transaction_date: string;
  created_at: string;
  updated_at: string | null;
  tenant_name: string | null;
  product_name: string | null;
}

export interface TransactionListResponse {
  total: number;
  items: Transaction[];
  summary: {
    total_count: number;
    total_gross: number;
    total_net: number;
    total_fee: number;
  };
}

export interface Payout {
  id: number;
  tenant_id: number | null;
  recipient_name: string;
  recipient_email: string | null;
  recipient_type: string;
  bank_info: Record<string, unknown> | null;
  total_amount: number;
  fee_amount: number;
  net_amount: number;
  currency: string;
  period_start: string;
  period_end: string;
  status: string;
  scheduled_date: string | null;
  paid_date: string | null;
  payout_method: string;
  external_payout_id: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string | null;
  tenant_name: string | null;
  distribution_count: number;
}

export interface PayoutListResponse {
  total: number;
  items: Payout[];
  summary: {
    pending_total: number;
    pending_count: number;
  };
}

export interface MonthlyReportSummary {
  year: number;
  month: number;
  tenant_id: number | null;
  product_id: number | null;
  total_transactions: number;
  total_gross_amount: number;
  total_net_amount: number;
  total_fee_amount: number;
  total_refunds: number;
  breakdown_by_source: Record<string, { count: number; amount: number }>;
  breakdown_by_recipient: Array<{
    name: string;
    email: string | null;
    total: number;
    count: number;
  }>;
}

export interface BillingCalculation {
  year: number;
  month: number;
  items: Array<{
    tenant_id: number;
    tenant_name: string;
    tenant_code: string;
    product_id: number;
    product_name: string;
    billing_model: string;
    breakdown: {
      monthly_fee: number;
      user_count: number;
      billable_users: number;
      per_user_fee: number;
      user_fee_total: number;
      revenue_share_percent: number;
      revenue_share_amount: number;
    };
    total: number;
    currency: string;
  }>;
  summary: {
    total_tenants: number;
    total_products: number;
    grand_total: number;
  };
}

// ===========================================
// Transaction API
// ===========================================

export const getTransactions = async (params?: {
  skip?: number;
  limit?: number;
  tenant_id?: number;
  product_id?: number;
  payment_source?: string;
  status?: string;
  year?: number;
  month?: number;
}): Promise<TransactionListResponse> => {
  const searchParams = new URLSearchParams();
  if (params?.skip) searchParams.append('skip', params.skip.toString());
  if (params?.limit) searchParams.append('limit', params.limit.toString());
  if (params?.tenant_id) searchParams.append('tenant_id', params.tenant_id.toString());
  if (params?.product_id) searchParams.append('product_id', params.product_id.toString());
  if (params?.payment_source) searchParams.append('payment_source', params.payment_source);
  if (params?.status) searchParams.append('status', params.status);
  if (params?.year) searchParams.append('year', params.year.toString());
  if (params?.month) searchParams.append('month', params.month.toString());

  const response = await api.get<TransactionListResponse>(`/admin/revenue/transactions?${searchParams.toString()}`);
  return response.data;
};

export const getTransaction = async (id: number): Promise<Transaction> => {
  const response = await api.get<Transaction>(`/admin/revenue/transactions/${id}`);
  return response.data;
};

// ===========================================
// Payout API
// ===========================================

export const getPayouts = async (params?: {
  skip?: number;
  limit?: number;
  tenant_id?: number;
  status?: string;
}): Promise<PayoutListResponse> => {
  const searchParams = new URLSearchParams();
  if (params?.skip) searchParams.append('skip', params.skip.toString());
  if (params?.limit) searchParams.append('limit', params.limit.toString());
  if (params?.tenant_id) searchParams.append('tenant_id', params.tenant_id.toString());
  if (params?.status) searchParams.append('status', params.status);

  const response = await api.get<PayoutListResponse>(`/admin/revenue/payouts?${searchParams.toString()}`);
  return response.data;
};

export const markPayoutPaid = async (id: number, externalId?: string): Promise<Payout> => {
  const params = externalId ? `?external_id=${encodeURIComponent(externalId)}` : '';
  const response = await api.post<Payout>(`/admin/revenue/payouts/${id}/mark-paid${params}`);
  return response.data;
};

export const generateMonthlyPayouts = async (
  year: number,
  month: number,
  tenantId?: number,
  minAmount?: number
): Promise<{
  created: Array<{ id: number; recipient: string; amount: number }>;
  skipped: Array<{ recipient: string; amount: number; reason: string }>;
  period: string;
}> => {
  const params = new URLSearchParams();
  params.append('year', year.toString());
  params.append('month', month.toString());
  if (tenantId) params.append('tenant_id', tenantId.toString());
  if (minAmount) params.append('min_amount', minAmount.toString());

  const response = await api.post(`/admin/revenue/payouts/generate-monthly?${params.toString()}`);
  return response.data;
};

// ===========================================
// Report API
// ===========================================

export const getMonthlyReport = async (
  year: number,
  month: number,
  tenantId?: number,
  productId?: number
): Promise<{ summary: MonthlyReportSummary }> => {
  const params = new URLSearchParams();
  params.append('year', year.toString());
  params.append('month', month.toString());
  if (tenantId) params.append('tenant_id', tenantId.toString());
  if (productId) params.append('product_id', productId.toString());

  const response = await api.get(`/admin/revenue/reports/monthly?${params.toString()}`);
  return response.data;
};

export const exportMonthlyCSV = async (
  year: number,
  month: number,
  exportType: 'transactions' | 'distributions' | 'payouts',
  tenantId?: number
): Promise<Blob> => {
  const params = new URLSearchParams();
  params.append('year', year.toString());
  params.append('month', month.toString());
  params.append('export_type', exportType);
  if (tenantId) params.append('tenant_id', tenantId.toString());

  const response = await api.get(`/admin/revenue/reports/monthly/csv?${params.toString()}`, {
    responseType: 'blob'
  });
  return response.data;
};

// ===========================================
// Billing API
// ===========================================

export const calculateMonthlyBilling = async (
  year: number,
  month: number,
  tenantId?: number
): Promise<BillingCalculation> => {
  const params = new URLSearchParams();
  params.append('year', year.toString());
  params.append('month', month.toString());
  if (tenantId) params.append('tenant_id', tenantId.toString());

  const response = await api.get(`/admin/revenue/billing/calculate-monthly?${params.toString()}`);
  return response.data;
};

export const generateInvoices = async (
  year: number,
  month: number,
  tenantId?: number
): Promise<{
  period: string;
  created: Array<{ transaction_id: number; tenant: string; product: string; amount: number }>;
  skipped: Array<{ tenant: string; product: string; reason: string }>;
  summary: { created_count: number; skipped_count: number; total_amount: number };
}> => {
  const params = new URLSearchParams();
  params.append('year', year.toString());
  params.append('month', month.toString());
  if (tenantId) params.append('tenant_id', tenantId.toString());

  const response = await api.post(`/admin/revenue/billing/generate-invoices?${params.toString()}`);
  return response.data;
};

// ===========================================
// PDF API
// ===========================================

export const downloadInvoicePDF = async (transactionId: number): Promise<Blob> => {
  const response = await api.get(`/admin/revenue/invoices/${transactionId}/pdf`, {
    responseType: 'blob'
  });
  return response.data;
};

export const downloadMonthlyReportPDF = async (
  year: number,
  month: number,
  tenantId?: number
): Promise<Blob> => {
  const params = new URLSearchParams();
  params.append('year', year.toString());
  params.append('month', month.toString());
  if (tenantId) params.append('tenant_id', tenantId.toString());

  const response = await api.get(`/admin/revenue/reports/monthly/pdf?${params.toString()}`, {
    responseType: 'blob'
  });
  return response.data;
};
